import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";

import {
    AddressFragment,
    CountryFragment,
    CreateAddressInput,
    CreateAddressMutation,
    CreateAddressMutationVariables,
    GetAvailableCountriesQuery,
} from "../../../common/generated-types";
import { GET_AVAILABLE_COUNTRIES } from "../../../common/graphql/documents.graphql";
import { DataService } from "../../../core/providers/data/data.service";
import { Dialog } from "../../../core/providers/modal/modal-types";
import { AddressFormComponent } from "../address-form/address-form.component";

import { CREATE_ADDRESS } from "./address-modal.graphql";

@Component({
    selector: "vsf-address-modal",
    templateUrl: "./address-modal.component.html",
    styleUrls: ["./address-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Default,
})
export class AddressModalComponent
    implements Dialog<AddressFragment>, OnInit, AfterViewInit, OnDestroy
{
    resolveWith: (result?: AddressFragment) => void;
    address: AddressFragment;
    title: string;
    hideCompany = false;
    availableCountries$: Observable<CountryFragment[]>;

    @ViewChild(AddressFormComponent)
    private addressFormComponent: AddressFormComponent;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.availableCountries$ = this.dataService
            .query<GetAvailableCountriesQuery>(GET_AVAILABLE_COUNTRIES)
            .pipe(map((data) => data.availableCountries));
    }

    ngAfterViewInit(): void {
        const form = this.addressFormComponent?.addressForm;
        if (form) {
            form.statusChanges
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.cdr.markForCheck());
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    cancel(): void {
        this.resolveWith(undefined);
    }

    save(): void {
        const form = this.addressFormComponent?.addressForm;
        if (!form?.valid) {
            return;
        }
        this.persistAddress(form.value as CreateAddressInput);
    }

    get saveDisabled(): boolean {
        return !this.addressFormComponent?.addressForm?.valid;
    }

    private persistAddress(value: CreateAddressInput): void {
        this.dataService
            .mutate<CreateAddressMutation, CreateAddressMutationVariables>(
                CREATE_ADDRESS,
                {
                    input: value,
                }
            )
            .subscribe((data) => {
                this.resolveWith(data.createCustomerAddress);
                this.cdr.markForCheck();
            });
    }
}
